/**
 * Core CRM domain types for the library.
 */

// Re-export types from main CRM types
import type { FeatureName } from '@/hooks/usePermissions';
import type { FilterCondition } from '@/lib/crm/filters/types';
import type {
  AssociationDetail,
  CrmObject,
  CrmObjectFilters,
  EntityTypeName,
} from '@/types/crm';
import { SYSTEM_ENTITY_TYPE_NAMES } from '@/types/crm';

import type { CardConfig } from '../card/types';

export type { AssociationDetail, CrmObject, EntityTypeName };

/**
 * Validate that an entity type name is valid.
 * Rejects *_timeline identifiers which are reserved for internal use.
 * Note: TypeScript type includes `org_${string}` for ergonomics, but runtime
 * validation (isValidEntityTypeName) enforces the _timeline restriction.
 *
 * The allowlist is `SYSTEM_ENTITY_TYPE_NAMES` from `@/types/crm` — the same
 * array `EntityTypeName` is derived from — so this validator can never drift
 * out of sync with the type.
 */
export function isValidEntityTypeName(name: string): name is EntityTypeName {
  return (
    !name.endsWith('_timeline') &&
    (name.startsWith('org_') ||
      (SYSTEM_ENTITY_TYPE_NAMES as readonly string[]).includes(name))
  );
}

/**
 * Event types for event-sourced CRM.
 */
export type EventTypeName =
  | 'ahoy_object_created'
  | 'ahoy_property_updated'
  | 'ahoy_errored'
  | 'ahoy_object_deleted'
  | 'ahoy_object_merged'
  | 'ahoy_object_restored'
  | 'ahoy_association_added'
  | 'ahoy_association_removed'
  | 'ahoy_email_sent'
  | 'ahoy_email_received';

/**
 * Base event structure with all required fields.
 */
export interface BaseEvent {
  entity_id: string;
  entity_type_name: string;
  event_type_name: EventTypeName;
  seq: number;
  idempotency_key: string;
  occurred_at: string;
  payload: Record<string, unknown>;
  source_type_name: string;
  source_id: string;
}

/**
 * Configuration for event building.
 */
export interface EventConfig {
  sourceType?: string;
  sourceId: string; // Required: must be a valid user ID for ahoy_user events
  startSeq?: number;
}

/**
 * A single record returned in the backend's association cardinality-violation
 * detail payload.  existing_owner is optional for backward-compatibility with
 * older error shapes that predate owner enrichment.
 */
export interface CardinalityConflict {
  entity_id: string;
  entity_type: string;
  display_name: string;
  association_type_name?: string;
  existing_owner?: {
    entity_id: string | null;
    entity_type: string;
    display_name: string;
  };
}

/**
 * List page configuration types
 */

/**
 * Column kind for generic column rendering.
 * - 'object_name': Renders avatar + display name together (sortable, primary identifier)
 * - 'property': Renders property value using PropertyValueLabel
 * - 'association': Renders association as CrmObjectTag(s)
 */
export type ColumnKind =
  | 'object_name'
  | 'property'
  | 'association'
  // Renders a property of an associated object per row; compound id
  // `<assoc>.<targetProp>`.
  | 'lookup'
  // Email-thread-specific: renders a clickable entity chip for the resolved
  // initiator association when present, falling back to the plain
  // ahoy_initiator_email text otherwise. Spans an association and a
  // property, so it can't be a plain 'association' or 'property' column.
  | 'email_initiator'
  // Renders the row's entity-type LABEL (e.g. "Event Automation"). Used by
  // kind-based lists that span multiple entity types so each row shows which
  // concrete type it is. Not backed by a property — the value comes from
  // object.entity_type_name.
  | 'type';

/** Column width type for React Aria table columns */
export type ColumnWidth = number | `${number}` | `${number}%` | `${number}fr`;

/**
 * Configuration for a table column.
 *
 * Uses declarative rendering via `kind` prop:
 * - 'object_name': Renders avatar + display name together (sortable, primary identifier)
 * - 'property': Renders property from `name` via PropertyValueLabel, label from property schema
 * - 'association': Renders associated objects from `name` via CrmObjectTag, label from association schema
 */
export interface ColumnConfig {
  /** Property or association type name (required for 'property' and 'association' kinds) */
  name?: string;
  /** Column kind for declarative rendering (object_name/property/association) */
  kind: ColumnKind;
  /** Whether column is sortable */
  sortable?: boolean;
  /** Column width (number, or string like "100", "50%", "1fr") */
  width?: ColumnWidth;
  /** Optional label override for the column header; takes priority over the schema-derived label. */
  label?: string;
  /** Set only for `kind: 'lookup'`; kept in sync with the compound `name`. */
  associationTypeName?: string;
  /** Set only for `kind: 'lookup'`; kept in sync with the compound `name`. */
  targetPropertyName?: string;
}

/**
 * Configuration for a filter field.
 */
export interface FilterFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'boolean';
  options?: Array<{ value: string; label: string }>;
}

/**
 * Configuration for a form field in list page configurations.
 *
 * Field type mapping:
 * - Backend field types (from schema): text, email, phone, domain, url, number, date, select
 * - FormFieldConfig types: text, email, tel, number, date, select, relationship, multi-relationship
 *
 * Notes:
 * - 'tel' maps to backend 'phone' field type
 * - 'relationship' is for single associations (returns entity_id string)
 * - 'multi-relationship' is for multiple associations (returns entity_ids array)
 * - 'domain' and 'url' backend types are rendered as 'text' inputs
 * - Note: 'textarea' is deliberately NOT part of FormFieldConfig since list page
 *   configurations don't use textarea fields
 */
export interface FormFieldConfig {
  name: string;
  label: string;
  type:
    | 'text'
    | 'email'
    | 'tel'
    | 'number'
    | 'date'
    | 'select'
    | 'relationship'
    | 'multi-relationship';
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  // TODO: Use canonical EntityType from @/lib/search/types.ts (see docs/technical-debt/entity-type-consolidation.md)
  entityType?: 'contact' | 'company' | 'deal'; // For relationship fields
}

/**
 * Metric definition for calculating and displaying metrics.
 */
export interface MetricDefinition {
  label: string;
  calculate: (data: CrmObject[], total: number) => number | string;
  change?: {
    value: number;
    trend: 'up' | 'down';
  };
}

/**
 * Stage metrics from backend aggregation (for pipeline views).
 *
 * Supports both currency-specific metrics (for deals) and generic property-driven metrics.
 */
export interface StageMetricsData {
  /**
   * Server-rollup item count for the stage. `undefined` when no rollup exists
   * for this entity type (e.g. note/custom pipelines, which have no count
   * rollup) — consumers fall back to the loaded item count in that case.
   */
  count?: number;
  totalAmount: Array<{ amount: number; unit: string | null }>;
  weightedAmount: Array<{ amount: number; unit: string | null }>;
  /** Generic property-driven metrics for custom entity types */
  properties?: Record<string, unknown>;
}

/**
 * Custom view configuration (e.g., pipeline view for deals).
 */
export interface CustomView {
  id: string;
  label: string;
  /** Optional feature flag required to show this view */
  featureFlag?: string;
  component: React.ComponentType<{
    data: CrmObject[];
    loading: boolean;
    onRefresh: () => void;
    /** Stage metrics from backend aggregation (for pipeline views) */
    stageMetrics?: Record<string, StageMetricsData> | null;
    /** Lookup map for associated objects when include_associated_objects=true */
    associatedObjects?: Record<string, CrmObject>;
    /** Whether the data is filtered (e.g., search is active) */
    isFiltered?: boolean;
    /** Selected pipeline entity_id passed from parent (for pipeline views) */
    selectedPipelineId?: string;
    /** Property name to sort cards by (for pipeline views) */
    sortBy?: string;
    /** Sort direction (for pipeline views) */
    sortOrder?: 'asc' | 'desc';
  }>;
  /** Whether this view should take full screen width */
  fullWidth?: boolean;
  /** Card config for the view — used to derive required associations and properties for the query API */
  cardConfig?: CardConfig;
}

/**
 * Context passed to row action functions (isDisabled, disabledReason).
 */
export interface RowActionContext {
  /** Enrichment eligibility info (only for contacts) */
  enrichmentEligibility?: {
    hasPhoneNumber: boolean;
    hasLinkedIn: boolean;
    hasPhoneEnrichment: boolean;
    /** Current enrichment status */
    status: 'idle' | 'pending' | 'completed' | 'failed';
    isEligible: boolean;
    disabledReason: string | undefined;
  };
}

/**
 * Row action configuration.
 */
export interface RowAction {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: (
    object: CrmObject,
    context?: RowActionContext
  ) => void | Promise<void>;
  variant?: 'default' | 'danger';
  /**
   * The feature or entity permission required for this action to be offered
   * in the row menu. Same contract and fail-closed default as
   * `BulkAction.requires`: when omitted, the action requires
   * `ahoy_property_updated` on the list's entity type, so a read-only seat is
   * never shown a row action that the server would refuse.
   */
  requires?: BulkActionRequirement;
  /** Function to check if action is disabled for this object */
  isDisabled?: (object: CrmObject, context?: RowActionContext) => boolean;
  /** Function to get reason when disabled (shown in tooltip) */
  disabledReason?: (
    object: CrmObject,
    context?: RowActionContext
  ) => string | undefined;
}

/**
 * Record action configuration for single-record views (slideout/full-page).
 * Similar to RowAction but without onClick (handled by RecordActionMenu).
 */
export interface RecordAction {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  variant?: 'default' | 'danger';
  /** Function to check if action is disabled for this object */
  isDisabled?: (object: CrmObject) => boolean;
  /** Function to get reason when disabled (shown in tooltip) */
  disabledReason?: (object: CrmObject) => string | undefined;
}

/**
 * Declares the feature or entity permission a bulk action requires to be
 * offered to the current user. See `BulkAction.requires` for the fail-closed
 * default applied when an action omits this.
 */
export type BulkActionRequirement =
  | { feature: FeatureName }
  | { permission: [EntityTypeName, EventTypeName] };

/**
 * Bulk action configuration for selected items.
 */
export interface BulkAction {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  variant?: 'default' | 'danger';
  onClick: (objects: CrmObject[]) => void | Promise<void>;
  isLoading?: boolean;
  /**
   * The feature or entity permission required for this action to be offered.
   * Fail-closed default when omitted: requires `ahoy_property_updated` write
   * permission on the list's entity type — an undeclared action never shows
   * to a user who can't write the entity type.
   */
  requires?: BulkActionRequirement;
  /** Whether this action requires confirmation before executing */
  requiresConfirmation?: boolean;
  /** Custom confirmation message (uses default if not provided) */
  confirmationMessage?: string;
  /** Custom confirmation title (uses default if not provided) */
  confirmationTitle?: string;
  /** Priority level for organizing actions (primary actions shown inline, secondary in dropdown) */
  priority?: 'primary' | 'secondary';
  /** Keyboard shortcut hint (e.g., "⌘⌫" for delete) */
  shortcut?: string;
  /**
   * Alternative handler for filter-based bulk operations (called instead of
   * onClick when in all/all-except mode). Receives the effective number of
   * records the action will target — `totalRecordCount - excludedEntityIds`,
   * computed from the list's total count — so the modal/toast can show an
   * accurate count without re-querying.
   */
  onClickWithFilters?: (
    filters: CrmObjectFilters,
    excludedEntityIds: string[],
    totalCount: number
  ) => void | Promise<void>;
}

/**
 * Tab configuration for list pages.
 */
export interface TabConfig {
  id: string;
  label: string;
}

/**
 * A client-side built-in view tab (e.g. "My booking links" / "All booking links").
 *
 * Unlike `defaultFilterConditions` (which renders as visible, removable chips),
 * a built-in view's `baselineConditions` are a HIDDEN, non-removable baseline
 * that is ANDed into every query while the view is active and is never shown
 * as a chip. User filters and saved views compose on top of it.
 *
 * `baselineConditions` are ALREADY RESOLVED — any per-user value (e.g. an
 * "owner = current user" clause) must be resolved by the page before it is
 * placed here (see MeetingTypesPage). While such a value is still resolving,
 * the page should omit that condition rather than emit an empty value.
 */
export interface BuiltInViewConfig {
  id: string;
  label: string;
  baselineConditions: FilterCondition[];
}

/**
 * The single active view on a list, unifying the two kinds of view a list can
 * show into one identity:
 *
 * - `builtin` — a client-computed default view declared in
 *   `ListPageConfig.builtInViews` (e.g. "My booking links"). Its baseline is a
 *   synchronous, hidden filter ANDed into the query.
 * - `saved` — a server-backed `SavedFilterView` (uuid), applied asynchronously
 *   with its own filter groups + column/sort state.
 *
 * `null` means the ephemeral "All" view — only reachable on lists that declare
 * no `builtInViews` (a list with built-in views always has one active).
 *
 * This identity is what the URL persists (a single `?savedView=` param: a raw
 * uuid for saved views, `builtin:<id>` for a non-default built-in view, and no
 * param for the default built-in / "All").
 */
export type ActiveListView =
  | { kind: 'builtin'; id: string }
  | { kind: 'saved'; id: string };

/**
 * Complete configuration for a list page.
 */
export interface ListPageConfig {
  entityType: EntityTypeName;
  /**
   * When set, the list fetches ALL entity types declaring this entity kind
   * via the by-kind query fan-out (server-side per-type access control +
   * merge + sort + paginate), instead of the single `entityType`. `entityType`
   * MUST still be set and is used as the REPRESENTATIVE type for permission
   * checks, property-schema/column resolution, and saved-view scoping — the
   * types sharing a kind are expected to share those schemas. Example:
   * `entityType: 'ahoy_event_automation'`, `entityKind: 'automation'`.
   */
  entityKind?: string;
  title: string;
  pluralTitle?: string;
  /** Icon name from @untitledui/icons (e.g., "User01", "Headphones01") */
  icon?: string;
  columns: ColumnConfig[];
  /**
   * Property or association type names never offered in the add-column
   * picker for this list — engine-owned fields (a config's JSON schema, an
   * automation's runtime state) and system edges (dependency graphs) that
   * are readable but have no business as a list column. The generic picker
   * already drops internal-read properties and infra targets; this is the
   * per-list layer for what is only noise HERE. Columns the config itself
   * declares are never affected.
   */
  hiddenColumnOptions?: readonly string[];
  filterFields?: FilterFieldConfig[];
  /** Array of property type names to show in create modal (e.g., ['ahoy_first_name', 'ahoy_email']) */
  createProperties?: string[];
  /** Array of association type names to show in create modal (e.g., ['ahoy_contact_to_company']) */
  createAssociations?: string[];
  metrics: MetricDefinition[];
  defaultFilters?: Partial<CrmObjectFilters>;
  /**
   * Conditions to seed as visible, removable filter chips on first load.
   *
   * Use this (instead of `defaultFilters.filter_groups`) when you want the
   * defaults to appear in the filter bar so the user can clear or modify
   * them. Examples: "active=true", "owner=me". Forced filters that should
   * NOT be removable (org-scoping, permission filters) belong on
   * `defaultFilters.filter_groups` instead.
   */
  defaultFilterConditions?: FilterCondition[];
  /**
   * Opt-in client-side built-in view tabs rendered in the saved-view strip.
   * When present, the strip shows one pill per built-in view instead of the
   * generic "All" pill. Callers MUST pass a stable/memoized array (identity
   * stability matters for downstream memoization).
   */
  builtInViews?: BuiltInViewConfig[];
  customViews?: CustomView[];
  // Additional filters specific to this object type (e.g., stage filter for deals)
  additionalFilters?: React.ComponentType<{
    filters: CrmObjectFilters;
    onFilterChange: (key: string, value: string | undefined) => void;
  }>;
  // Row actions (e.g., edit, delete)
  rowActions?: RowAction[];
  // Bulk actions for selected items (e.g., bulk delete, bulk export)
  bulkActions?: BulkAction[];
  // UI configuration
  showTabs?: boolean; // Whether to show navigation tabs (default: true)
  tabs?: TabConfig[]; // Custom tabs to show (defaults to standard set if not specified)
  defaultTab?: string; // Default tab to select on load (default: 'overview')
  showTimeFilter?: boolean; // Whether to show time range filter buttons (default: true)
  emptyStateComponent?: React.ComponentType<{ onCreateClick: () => void }>; // Custom empty state component
  /**
   * Custom copy for the table's no-data empty state. Use when the default
   * "No <entities> yet / Get started by creating your first <entity>" doesn't
   * fit — e.g. system-created entities that can't be created directly.
   */
  emptyState?: { heading?: string; description?: string };
  /** Optional React node rendered in the header action area (e.g., pipeline field selector) */
  headerExtra?: React.ReactNode;
  /**
   * Association type name linking the entity to its pipeline *stage* (e.g.,
   * 'ahoy_deal_to_deal_pipeline_stage'). Presence of this field is what enables
   * the board/pipeline view (CTA + `?view=board` route).
   *
   * Only set this for entities that are pipeline-rendering under the declarative
   * kind rule — entity_kind includes `case` (see `entityTypeHasPipeline` in
   * hooks/useStages.ts). Stage-only taxonomies like
   * ahoy_contact lifecycle stages have NO pipeline-tier entity, so the pipelines
   * schema endpoint can't serve a board for them; leaving this unset keeps them
   * in the table view.
   * The lifecycle-stage *column* and bulk stage-change actions do not depend on
   * this field, so omitting it loses nothing for stage-only entities.
   */
  stageAssociationTypeName?: string;
  /**
   * Default view to render when no `?view` query param is present.
   * Falls back to 'pipeline' when stageAssociationTypeName is set, else 'table'.
   */
  defaultView?: 'table' | 'pipeline';
}
